import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { setFormatLocale } from '@/lib/format';
import { de, type Dictionary } from './de';
import { en } from './en';

export type Locale = 'de' | 'en';

const DICTIONARIES: Record<Locale, Dictionary> = { de, en };

export const LOCALES: Array<{ code: Locale; label: string; short: string }> = [
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'en', label: 'English', short: 'EN' },
];

const STORAGE_KEY = 'fluxpanel-locale';

/** Punktnotation über das Wörterbuch, z.B. 'peers.limitReached'. */
type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : Leaves<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

export type TranslationKey = Leaves<Dictionary>;

interface I18nState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Übersetzt einen Schlüssel und ersetzt {platzhalter}. */
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nState | null>(null);

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'de';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'de' || stored === 'en') return stored;
  // Browsersprache als Vorgabe, im Zweifel Deutsch
  return window.navigator.language.toLowerCase().startsWith('en') ? 'en' : 'de';
}

function resolve(dictionary: Dictionary, key: string): string | undefined {
  const value = key
    .split('.')
    .reduce<unknown>((node, part) => (node as Record<string, unknown>)?.[part], dictionary);
  return typeof value === 'string' ? value : undefined;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  // Zahlen- und Datumsformate folgen der Oberflaechensprache
  useEffect(() => {
    setFormatLocale(locale);
    document.documentElement.setAttribute('lang', locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const t = useCallback<I18nState['t']>(
    (key, values) => {
      // Fehlender Schlüssel in der Zweitsprache fällt auf Deutsch zurück,
      // damit nie ein roher Schlüssel in der Oberfläche erscheint.
      const template = resolve(DICTIONARIES[locale], key) ?? resolve(de, key) ?? key;
      if (!values) return template;
      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        values[name] === undefined ? match : String(values[name]),
      );
    },
    [locale],
  );

  const value = useMemo<I18nState>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nState {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n muss innerhalb von I18nProvider verwendet werden');
  return context;
}

/** Kurzform für Komponenten, die nur übersetzen. */
export function useT(): I18nState['t'] {
  return useI18n().t;
}
